import { context, propagation, trace, type Context } from "@opentelemetry/api";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
    BasicTracerProvider,
    BatchSpanProcessor,
    type SpanProcessor,
} from "@opentelemetry/sdk-trace-base";
import type { ReadableSpan, Span } from "@opentelemetry/sdk-trace";
import {
    ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
    ATTR_SERVICE_NAME,
    ATTR_URL_FULL,
} from "@opentelemetry/semantic-conventions";

let tracingStarted = false;

export const stripUrlQueryAndHash = (value: string): string => {
    const withoutHash = value.split("#")[0] ?? value;
    return withoutHash.split("?")[0] ?? withoutHash;
};

export const readEnvValue = (
    configured: string | undefined,
): string | undefined => {
    const trimmed = configured?.trim() ?? "";

    return trimmed.length > 0 ? trimmed : undefined;
};

const escapeRegExp = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

class StripUrlQuerySpanProcessor implements SpanProcessor {
    public onStart(_span: Span, _parentContext: Context): void {}

    public onEnding(span: Span): void {
        const url = span.attributes[ATTR_URL_FULL];

        if (typeof url !== "string") {
            return;
        }

        span.setAttribute(ATTR_URL_FULL, stripUrlQueryAndHash(url));
    }

    public onEnd(_span: ReadableSpan): void {}

    public forceFlush(): Promise<void> {
        return Promise.resolve();
    }

    public shutdown(): Promise<void> {
        return Promise.resolve();
    }
}

const apiPropagationUrls = (): RegExp[] => {
    const apiBase = readEnvValue(import.meta.env.VITE_ADMIN_URL);

    if (!apiBase) {
        return [];
    }

    return [new RegExp(`^${escapeRegExp(apiBase)}`)];
};

export const setupTracing = (): void => {
    if (tracingStarted || typeof window === "undefined") {
        return;
    }

    const tracesEndpoint = readEnvValue(
        import.meta.env.VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT,
    );
    const deploymentEnvironment = readEnvValue(
        import.meta.env.VITE_ENVIRONMENT,
    );
    const serviceName = readEnvValue(import.meta.env.VITE_OTEL_SERVICE_NAME);

    if (!tracesEndpoint || !deploymentEnvironment || !serviceName) {
        return;
    }

    tracingStarted = true;

    const provider = new BasicTracerProvider({
        resource: resourceFromAttributes({
            [ATTR_SERVICE_NAME]: serviceName,
            [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: deploymentEnvironment,
        }),
        spanProcessors: [
            new StripUrlQuerySpanProcessor(),
            new BatchSpanProcessor(
                new OTLPTraceExporter({ url: tracesEndpoint }),
            ),
        ],
    });

    context.setGlobalContextManager(new ZoneContextManager().enable());
    propagation.setGlobalPropagator(new W3CTraceContextPropagator());
    trace.setGlobalTracerProvider(provider);

    const httpInstrumentation = {
        ignoreUrls: [new RegExp(escapeRegExp(tracesEndpoint))],
        propagateTraceHeaderCorsUrls: apiPropagationUrls(),
    };

    registerInstrumentations({
        instrumentations: getWebAutoInstrumentations({
            "@opentelemetry/instrumentation-fetch": httpInstrumentation,
            "@opentelemetry/instrumentation-xml-http-request":
                httpInstrumentation,
        }),
    });
};
